import * as React from 'react'
import { FC, useMemo, useState } from 'react';
import { AppointmentData, ProfessionalData, DayOfWeekEnum, DateInterval, ProfessionalAvailability } from '../../models';
import { DateRangeSelectorState } from '../../components/DateRangeSelector';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { AutocompleteTextField } from '../../components/Forms/fields';
import { Alert } from 'reactstrap';
import { ResponsiveBar } from '@nivo/bar';
import { CSVLink } from 'react-csv';
import { useSelector } from 'react-redux';
import { getGeneralSettings } from '../../store/settings/selectors';
import { serviceName } from '../../shared/appointmint/appointment';
import { getEntitiesDataItems } from '../../store/entities/selectors';
import { minutesBetweenTimes } from '../../shared/utils';
import moment from 'moment';
import { useBreakpoints } from '../../shared/hooks/useBreakpoints';

// Stable empty array to prevent infinite render loops
const EMPTY_PROFESSIONALS: ProfessionalData[] = [];

type OccupancyMode = 'professionals' | 'rooms'

// Helper to check if a date is within a DateInterval
const isDateInInterval = (date: string, interval: DateInterval): boolean => {
    return date >= interval.from && date <= interval.to;
}

// Helper to get the active ProfessionalAvailability for a given date
const getActiveAvailability = (date: string, availabilities?: ProfessionalAvailability[]): ProfessionalAvailability | undefined => {
    if (!availabilities || availabilities.length === 0) return undefined;
    // Sort by 'from' date descending and find the first one that applies
    const sorted = [...availabilities].sort((a, b) => b.from.localeCompare(a.from));
    return sorted.find(av => date >= av.from);
}

const getAvailableMinutesForSchedule = (
    date: string,
    dayOfWeek: DayOfWeekEnum,
    timeAvailability: ProfessionalAvailability[] | undefined,
    absences: DateInterval[] | undefined,
    availabilityExtra: DateInterval[] | undefined,
): number => {
    if (absences?.some(absence => isDateInInterval(date, absence))) {
        return 0;
    }

    if (availabilityExtra?.some(extra => isDateInInterval(date, extra))) {
        return 24 * 60;
    }

    const activeAvailability = getActiveAvailability(date, timeAvailability);
    if (!activeAvailability) return 0;

    return activeAvailability.timeSlots
        .filter(slot => slot.dayOfWeek === dayOfWeek)
        .reduce((sum, slot) => sum + minutesBetweenTimes(slot.start, slot.end), 0);
};

// Calculate total available minutes for a professional on a specific date.
// When delegated to multiple branches, sums availability across all of them.
const getProfessionalAvailableMinutesForDate = (
    date: string,
    professional: ProfessionalData
): number => {
    const dayOfWeek = moment(date).day() as DayOfWeekEnum;
    const branchEntries = Object.entries(professional.branchTimeAvailability ?? {});

    if (branchEntries.length > 0) {
        return branchEntries.reduce((total, [branchId, branchAvailability]) => total + getAvailableMinutesForSchedule(
            date,
            dayOfWeek,
            branchAvailability,
            professional.branchAbsences?.[branchId],
            professional.branchAvailabilityExtra?.[branchId],
        ), 0);
    }

    return getAvailableMinutesForSchedule(
        date,
        dayOfWeek,
        professional.timeAvailability,
        professional.absences,
        professional.availabilityExtra,
    );
}

// Calculate total appointment minutes for appointments
const getAppointmentMinutes = (appointments: AppointmentData[]): number => {
    return appointments.reduce((sum, apt) => {
        return sum + minutesBetweenTimes(apt.start, apt.end);
    }, 0);
}

// Get all unique dates in the window
const getDatesInWindow = (window: DateRangeSelectorState): string[] => {
    const dates: string[] = [];

    // Work in UTC to avoid timezone offsets causing infinite loops (e.g., US timezones)
    let cursor = moment.utc(window.from, 'YYYY-MM-DD', true);
    const end = moment.utc(window.to, 'YYYY-MM-DD', true);

    if (!cursor.isValid() || !end.isValid()) return dates;

    while (cursor.isSameOrBefore(end, 'day')) {
        dates.push(cursor.format('YYYY-MM-DD'));
        cursor = cursor.add(1, 'day');
    }

    return dates;
}

const getProfessionalOccupancyData = (
    appointments: AppointmentData[],
    professionals: ProfessionalData[],
    window: DateRangeSelectorState,
    filterValues: string[],
    typeToName: (type: string) => string,
): Record<string, { occupancyRate: number; appointmentMinutes: number; availableMinutes: number; appointments: AppointmentData[] }> => {

    const filter = (a: AppointmentData) => !filterValues.length || !filterValues.some(v => ![
        a.clientName ?? '',
        a.professionalName ?? '',
        typeToName(a.type),
        a.room,
    ].includes(v));

    const filteredAppointments = appointments.filter(a => filter(a) && a.date >= window.from && a.date <= window.to);

    // Group appointments by professional
    const appointmentsByProfessional = filteredAppointments.reduce<Record<string, AppointmentData[]>>((acc, apt) => {
        const profName = apt.professionalName ?? 'Unknown';
        if (!acc[profName]) acc[profName] = [];
        acc[profName].push(apt);
        return acc;
    }, {});

    const dates = getDatesInWindow(window);

    const result: Record<string, { occupancyRate: number; appointmentMinutes: number; availableMinutes: number; appointments: AppointmentData[] }> = {};

    professionals.forEach(prof => {
        const profAppointments = appointmentsByProfessional[prof.name] || [];

        // Calculate total available minutes across all dates
        const totalAvailableMinutes = dates.reduce((sum, date) => {
            return sum + getProfessionalAvailableMinutesForDate(date, prof);
        }, 0);

        const totalAppointmentMinutes = getAppointmentMinutes(profAppointments);

        if (totalAvailableMinutes > 0) {
            result[prof.name] = {
                occupancyRate: totalAppointmentMinutes / totalAvailableMinutes,
                appointmentMinutes: totalAppointmentMinutes,
                availableMinutes: totalAvailableMinutes,
                appointments: profAppointments,
            };
        }
    });

    return result;
}

const getRoomOccupancyData = (
    appointments: AppointmentData[],
    window: DateRangeSelectorState,
    openingHour: string,
    closingHour: string,
    filterValues: string[],
    typeToName: (type: string) => string,
): Record<string, { occupancyRate: number; appointmentMinutes: number; availableMinutes: number; appointments: AppointmentData[] }> => {

    const filter = (a: AppointmentData) => !filterValues.length || !filterValues.some(v => ![
        a.clientName ?? '',
        a.professionalName ?? '',
        typeToName(a.type),
        a.room,
    ].includes(v));

    const filteredAppointments = appointments.filter(a => filter(a) && a.date >= window.from && a.date <= window.to);

    // Group appointments by room
    const appointmentsByRoom = filteredAppointments.reduce<Record<string, AppointmentData[]>>((acc, apt) => {
        const room = apt.room || 'Unknown';
        if (!acc[room]) acc[room] = [];
        acc[room].push(apt);
        return acc;
    }, {});

    const dates = getDatesInWindow(window);
    const dailyMinutes = minutesBetweenTimes(openingHour, closingHour);
    const totalAvailableMinutes = dailyMinutes * dates.length;

    const result: Record<string, { occupancyRate: number; appointmentMinutes: number; availableMinutes: number; appointments: AppointmentData[] }> = {};

    Object.entries(appointmentsByRoom).forEach(([room, roomAppointments]) => {
        const totalAppointmentMinutes = getAppointmentMinutes(roomAppointments);

        result[room] = {
            occupancyRate: totalAppointmentMinutes / totalAvailableMinutes,
            appointmentMinutes: totalAppointmentMinutes,
            availableMinutes: totalAvailableMinutes,
            appointments: roomAppointments,
        };
    });

    return result;
}

export const AnalyticsOcupancy: FC<{
    appointments: AppointmentData[],
    window: DateRangeSelectorState,
}> = ({
    appointments,
    window,
}) => {
    const t = useTranslations();
    const bp = useBreakpoints();
    const generalSettings = useSelector(getGeneralSettings);
    const { openningHour = '09:00', closingHour = '18:00' } = generalSettings;
    const professionals = useSelector(getEntitiesDataItems<ProfessionalData>('professionals')) ?? EMPTY_PROFESSIONALS;

    const [mode, setMode] = useState<OccupancyMode>('professionals');
    const [unit, setUnit] = useState<'percentage'|'hours'>('percentage')
    const [filter, setFilter] = useState<string[]>([]);

    const formatValue = React.useCallback((value: string | number) => {
        if (typeof value !== 'number') return value;
        return `${Math.round(value * 1000) / 10} %`;
    }, []);

    const formatMinutes = React.useCallback((minutes: number) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
    }, []);

    const possibleFilterValues = useMemo<string[]>(() => [...new Set([
        ...appointments.map(a => serviceName(a.type, generalSettings)),
        ...appointments.map(a => a.room),
        ...appointments.map(a => a.professionalName ?? ''),
        ...appointments.map(a => a.clientName ?? ''),
    ])], [appointments, generalSettings]);

    const professionalOccupancy = useMemo(
        () => getProfessionalOccupancyData(appointments, professionals, window, filter, t => serviceName(t, generalSettings)),
        [appointments, professionals, window, filter, generalSettings]
    );

    const roomOccupancy = useMemo(
        () => getRoomOccupancyData(appointments, window, openningHour, closingHour, filter, t => serviceName(t, generalSettings)),
        [appointments, window, openningHour, closingHour, filter, generalSettings]
    );

    const occupancyData = mode === 'professionals' ? professionalOccupancy : roomOccupancy;

    const totalsData = useMemo(() => Object.entries(occupancyData)
        .map(([label, data]) => {
            if (unit === 'hours') {
                return {
                    label,
                    [t(TK.appointmentTime)]: data.appointmentMinutes / 60,
                    [t(TK.availableTime)]: data.availableMinutes / 60,
                };
            } else {
                return {
                    label,
                    [t(TK.value)]: data.occupancyRate,
                };
            }
        })
        .sort((a, b) => {
            if (unit === 'hours') {
                return (a[t(TK.appointmentTime)] as number) - (b[t(TK.appointmentTime)] as number);
            } else {
                return (a[t(TK.value)] as number) - (b[t(TK.value)] as number);
            }
        }), [occupancyData, unit, t]);

    const totalOccupancy = useMemo(() => totalsData.length > 0
        ? (unit === 'hours'
            ? totalsData.reduce((sum, item) => sum + (item[t(TK.appointmentTime)] as number), 0) / totalsData.length
            : totalsData.reduce((sum, item) => sum + (item[t(TK.value)] as number), 0) / totalsData.length)
        : 0, [totalsData, unit, t]);

    const detailedData = useMemo<Record<string, string | number>[]>(() => totalsData.map(item => {
        const baseData = {
            [mode === 'professionals' ? t(TK.professional) : t(TK.room)]: item.label,
        };

        if (unit === 'hours') {
            return {
                ...baseData,
                [t(TK.appointmentTime)]: `${(occupancyData[item.label].appointmentMinutes / 60).toFixed(1)}h`,
                [t(TK.availableTime)]: `${(occupancyData[item.label].availableMinutes / 60).toFixed(1)}h`,
                [t(TK.occupancyRate)]: formatValue(occupancyData[item.label].occupancyRate),
                [t(TK.numberOfAppointments)]: occupancyData[item.label].appointments.length,
            };
        } else {
            return {
                ...baseData,
                [t(TK.occupancyRate)]: formatValue(occupancyData[item.label].occupancyRate),
                [t(TK.appointmentTime)]: formatMinutes(occupancyData[item.label].appointmentMinutes),
                [t(TK.availableTime)]: formatMinutes(occupancyData[item.label].availableMinutes),
                [t(TK.numberOfAppointments)]: occupancyData[item.label].appointments.length,
            };
        }
    }), [totalsData, occupancyData, unit, mode, t, formatValue, formatMinutes]);

    const unitMap = useMemo(() => ({
        [t(TK.percentage)]: TK.percentage,
        [t(TK.hours)]: TK.hours,
        [TK.percentage]: t(TK.percentage),
        [TK.hours]: t(TK.hours),
    }), [t]);

    const unitOptions = useMemo(() => Object.keys(unitMap).slice(0, Object.keys(unitMap).length / 2), [unitMap]);

    const groupByOptions = useMemo(() => [t(TK.professionals), t(TK.rooms)], [t]);

    return (<>
        <Alert color='secondary' style={{ marginTop: 'var(--spacer-S)' }}>
            {mode === 'professionals'
                ? t(TK.occupancyRateProfessionalsDescription)
                : t(TK.occupancyRateRoomsDescription)}
        </Alert>

        <div style={{ display: 'flex', gap: 'var(--spacer-S)', flexDirection: 'column', marginBottom: 'var(--spacer-M)' }}>

            <div style={{display: 'flex', gap: 'var(--spacer-S)', flexDirection: bp.sm ? 'column' : 'row', marginTop: 'var(--spacer-S)'}}>
                <AutocompleteTextField
                    label={t(TK.groupBy)}
                    style={{ flexGrow: 1 }}
                    options={groupByOptions}
                    value={mode === 'professionals' ? t(TK.professionals) : t(TK.rooms)}
                    onValueChanged={val => setMode(val === t(TK.professionals) ? 'professionals' : 'rooms')}
                />
                <AutocompleteTextField
                    style={{flexGrow: 1}}
                    label={t(TK.unit)}
                    options={unitOptions}
                    value={unitMap[unit]}
                    onValueChanged={v => setUnit(unitMap[v] as any ?? 'percentage')}
                />
            </div>

            <AutocompleteTextField
                label={t(TK.filter)}
                style={{ flexGrow: 1 }}
                options={possibleFilterValues}
                multiSelect
                values={filter}
                onValuesChanged={setFilter}
            />
        </div>

        <hr />
        <h2 style={{ textAlign: 'center' }}>
            <b>{t(TK.total)}:</b> {unit === 'hours'
                ? `${totalOccupancy.toFixed(1)}h`
                : formatValue(totalOccupancy)}
        </h2>
        <hr />

        {totalsData.length === 0 ? (
            <Alert color='info'>{t(TK.noDataAvailable)}</Alert>
        ) : (
            <>
                <div style={{ height: `${150 + totalsData.length * 20}px`, width: '100%' }}>
                    <ResponsiveBar
                        data={totalsData}
                        keys={unit === 'hours' ? [t(TK.appointmentTime), t(TK.availableTime)] : [t(TK.value)]}
                        labelFormat={unit === 'hours' ? (value) => `${Number(value).toFixed(1)}h` : formatValue}
                        tooltipFormat={unit === 'hours' ? (value) => `${Number(value).toFixed(1)}h` : formatValue}
                        labelSkipWidth={35}
                        labelSkipHeight={12}
                        indexBy="label"
                        margin={{
                            top: 10,
                            right: unit === 'hours' ? 120 : 10,
                            bottom: 50,
                            left: totalsData.reduce((curr, item) => curr > (item.label as string).length ? curr : (item.label as string).length, 0) * 5 + 15,
                        }}
                        padding={0.3}
                        colors={{ scheme: 'nivo' }}
                        layout="horizontal"
                        groupMode='grouped'
                        axisBottom={{
                            legendOffset: 36,
                            legendPosition: 'middle',
                        }}
                        enableGridX={true}
                    />
                </div>

                <hr />

                <h3>{t(TK.detailed)}</h3>

                <div style={{ marginTop: 'var(--spacer-S)' }}>
                    {Object.entries(occupancyData)
                        .sort(([, a], [, b]) => unit === 'hours'
                            ? b.appointmentMinutes - a.appointmentMinutes
                            : b.occupancyRate - a.occupancyRate)
                        .map(([name, data]) => (
                            <div key={name} style={{ marginBottom: 'var(--spacer-M)' }}>
                                <h5>
                                    {name} - {unit === 'hours'
                                        ? `${(data.appointmentMinutes / 60).toFixed(1)}h / ${(data.availableMinutes / 60).toFixed(1)}h`
                                        : formatValue(data.occupancyRate)}
                                </h5>
                                <div style={{ marginLeft: 'var(--spacer-M)', color: 'var(--text-muted)' }}>
                                    {unit === 'percentage' && (
                                        <>
                                            <div>
                                                <strong>{t(TK.appointmentTime)}:</strong> {formatMinutes(data.appointmentMinutes)}
                                            </div>
                                            <div>
                                                <strong>{t(TK.availableTime)}:</strong> {formatMinutes(data.availableMinutes)}
                                            </div>
                                        </>
                                    )}
                                    {unit === 'hours' && (
                                        <>
                                            <div>
                                                <strong>{t(TK.appointmentTime)}:</strong> {(data.appointmentMinutes / 60).toFixed(1)}h
                                            </div>
                                            <div>
                                                <strong>{t(TK.availableTime)}:</strong> {(data.availableMinutes / 60).toFixed(1)}h
                                            </div>
                                            <div>
                                                <strong>{t(TK.occupancyRate)}:</strong> {formatValue(data.occupancyRate)}
                                            </div>
                                        </>
                                    )}
                                    <div>
                                        <strong>{t(TK.numberOfAppointments)}:</strong> {data.appointments.length}
                                    </div>
                                </div>
                            </div>
                        ))}
                </div>

                <CSVLink
                    data={detailedData}
                    separator=';'
                    target='_blank'
                    filename={`occupancy-${mode}-${window.from}-${window.to}.csv`}
                >
                    {t(TK.exportData)}
                </CSVLink>
            </>
        )}
    </>);
}
